import axios from 'axios';

import router from '@/router';
import store from '@/store';

const FILTER_KEY_SUFFIX = '_filter';

/**
 * Формирует ключ хранения фильтра в localStorage.
 * Для компонента DictionaryItems ключ дополняется именем справочника.
 *
 * @param name — имя компонента ($options.name)
 * @param additionalKey — доп. суффикс ключа
 * @param dictionaryName — имя справочника (только для DictionaryItems)
 */
export const getFilterKey = (name: string, additionalKey = '', dictionaryName = ''): string =>
  name !== 'DictionaryItems'
    ? name + FILTER_KEY_SUFFIX + (additionalKey ?? '')
    : `${name}_${dictionaryName}${FILTER_KEY_SUFFIX}${additionalKey ?? ''}`;

/** Сохраняет фильтр в localStorage. */
export const saveFilter = (name: string, filter: unknown, additionalKey = '', dictionaryName = ''): void => {
  localStorage.setItem(getFilterKey(name, additionalKey, dictionaryName), JSON.stringify(filter));
};

/** Удаляет фильтр из localStorage. */
export const removeFilter = (name: string, additionalKey = '', dictionaryName = ''): void => {
  localStorage.removeItem(getFilterKey(name, additionalKey, dictionaryName));
};

/** Запрашивает сохранённое значение фильтра для роли/маршрута. */
export const getFilterValue = (roleId: number | string, route: string, searchGroup: boolean) =>
  axios.get(`/api/filter/filterValue?route=${route}&roleId=${roleId}&searchGroup=${searchGroup}`);

/** Обновляет значение фильтра на сервере. */
export const updateFilterValue = (item: unknown) => axios.put('/api/filter/updateFilterValue', item);

/**
 * Загружает фильтр: сначала из localStorage, иначе — с сервера по роли пользователя
 * (roleId берётся из стора, маршрут — из роутера).
 *
 * @param name — имя компонента ($options.name)
 * @param additionalKey — доп. суффикс ключа
 * @param dictionaryName — имя справочника (только для DictionaryItems)
 */
export const loadFilter = async (name: string, additionalKey = '', dictionaryName = ''): Promise<unknown> => {
  const filter = localStorage.getItem(getFilterKey(name, additionalKey, dictionaryName));
  if (filter && filter != undefined) return JSON.parse(filter);

  const roleId = (store.state as { auth?: { oidc?: { profile?: { roleIds?: (number | string)[] } } } }).auth?.oidc
    ?.profile?.roleIds?.[0];
  if (roleId) {
    const response = await getFilterValue(roleId, router.currentRoute.value.path, true);
    const filterItem = response.data;
    if (filterItem) return JSON.parse(filterItem.value);
  }

  return undefined;
};

/**
 * Проверяет, что фильтр не пустой (есть хотя бы одно непустое значение).
 *
 * @param filter — объект фильтра
 * @param skipKeys — ключи, которые нужно пропустить
 */
export const isFilterNotEmpty = (filter: Record<string, unknown>, skipKeys?: string[]): boolean => {
  let isEmpty = true;
  for (const key in filter) {
    if (skipKeys?.includes(key)) continue;

    const value = filter[key];
    if ((Array.isArray(value) || typeof value === 'string') && value.length > 0) {
      isEmpty = false;
      break;
    }
    if (!isNaN(value as number) && (value as number) > 0) {
      isEmpty = false;
      break;
    }
  }
  return !isEmpty;
};
